import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Card, CardContent, Grid, GridColumn, Heading, Icon, Notice, Separator, Spinner, Table, TableCell, Text } from 'telia-front-react';
import RoamingNumberStore from '../stores/RoamingNumberStore';
import { formatDecimalPlacesRoundingUp, replaceDotWithComma } from '../util/PriceUtil';
import { ServicePrice } from './RoamingInterfaces';

interface RoamingNumberResultsProps {
  roamingNumberStore?: RoamingNumberStore;
}

interface ServiceRowValues {
  key: string;
  icon: string;
  serviceName: string;
  price: string;
  order: number;
}

@observer
class RoamingNumberResults extends React.Component<RoamingNumberResultsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private getTableRow(serviceRowValues: ServiceRowValues): JSX.Element {
    return (
      <tr key={serviceRowValues.key}>
        <TableCell>
          <Grid noWrap={true} align={['middle-xs']} verticalGutter="none">
            <GridColumn width={['min']}>
              <Icon modifiers={serviceRowValues.icon} size="xs" />
            </GridColumn>
            <GridColumn width={['max']}>
              <strong>{serviceRowValues.serviceName}</strong>
            </GridColumn>
          </Grid>
        </TableCell>
        <TableCell className="text-right">{serviceRowValues.price}</TableCell>
      </tr>
    );
  }

  private getTableBody(): JSX.Element {
    const {
      roamingNumberStore: { selectedCountry }
    } = this.props;
    const serviceRowValues = selectedCountry.prices
      .map((price) => this.getServiceRowValues(price, selectedCountry.eea))
      .sort(this.sortServiceRows)
      .map((row) => this.getTableRow(row));
    return <tbody>{serviceRowValues}</tbody>;
  }

  public getServiceRowValues(price: ServicePrice, eea: boolean) {
    const translate = this.context.translate;
    return {
      order: this.getServiceRowOrder(price.priceType),
      key: `${price.priceType}-${price.price}`,
      icon: this.getServiceIcon(price.priceType),
      serviceName: translate(`roamingNumberResults.serviceName.${price.priceType.toUpperCase()}`),
      price: this.getPriceString(price.priceWithKM, price.priceType, eea)
    };
  }

  private getServiceIcon(priceType: string) {
    switch (priceType.toUpperCase()) {
      case 'DEFAULT':
      case 'FIX':
        return '#phone';
      case 'MOB':
        return '#mobile';
      case 'PREMIUM':
        return '#phone-favorite';
      case 'SMS':
        return '#chat';
      default:
        return null;
    }
  }

  private sortServiceRows(row1: ServiceRowValues, row2: ServiceRowValues) {
    return row1.order - row2.order;
  }

  private getServiceRowOrder(priceType: string) {
    switch (priceType.toUpperCase()) {
      case 'MOB':
        return 0;
      case 'FIX':
        return 1;
      case 'PREMIUM':
        return 2;
      case 'DEFAULT':
        return 3;
      case 'SMS':
        return 4;
      default:
        return 5;
    }
  }

  public getPriceString(price: number, type: string, eea: boolean) {
    const translate = this.context.translate;
    const decimalPlaces = eea ? 3 : 2;
    const priceString = replaceDotWithComma(formatDecimalPlacesRoundingUp(price, decimalPlaces));
    const priceUnit = type.toUpperCase() === 'SMS' ? translate('roamingNumberResults.priceUnit.sms') : translate('roamingNumberResults.priceUnit.call');

    return `${priceString} ${priceUnit}`;
  }

  public render() {
    const translate = this.context.translate;
    const {
      roamingNumberStore,
      roamingNumberStore: { selectedCountry }
    } = this.props;
    if (roamingNumberStore.noResults || roamingNumberStore.priceByPrefixList.status === 'FAILED') {
      return <Notice>{translate('roamingNumberResults.noResults')}</Notice>;
    }
    if (!roamingNumberStore.selectedCountry) {
      return null;
    }
    return (
      <Card>
        <CardContent>
          <Heading element="h2">{selectedCountry.fromPrefix ? selectedCountry.searchTerm : selectedCountry.name}</Heading>
          <Separator spacing="small" color="invisible" />
          {roamingNumberStore.isPriceByPrefixListLoading ? (
            <Spinner />
          ) : (
            <Text>
              <p className="h4">
                {translate('roamingNumberResults.callingCode')}:
                <strong>
                  {' '}
                  +{selectedCountry.callingCode}
                  {selectedCountry.fromPrefix ? ` (${selectedCountry.name})` : ''}
                </strong>
              </p>
              <Table>{this.getTableBody()}</Table>
            </Text>
          )}
        </CardContent>
      </Card>
    );
  }
}

export default inject('roamingNumberStore')(RoamingNumberResults);
